/**
 * Hooks de configuração WhatsApp por tenant (superadmin).
 *
 * Modelo: conta master YCloud + WABA-per-tenant.
 * RLS: superadmin tem `for all` em tenant_whatsapp (migration 20260524000100).
 *
 * Campos chave:
 *  - display_phone   → E.164, campo "from" no payload YCloud
 *  - phone_number_id → roteamento do webhook inbound
 *  - waba_id         → ID da WABA no dashboard YCloud/Meta
 *  - status          → pending | active | suspended | inactive
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

// ── Tipos ────────────────────────────────────────────────────────────────────

export type WhatsAppStatus = 'pending' | 'active' | 'suspended' | 'inactive';

export const WA_STATUS_LABELS: Record<WhatsAppStatus, string> = {
  pending:   'Aguardando ativação',
  active:    'Ativo',
  suspended: 'Suspenso',
  inactive:  'Inativo',
};

export const WA_STATUS_COLORS: Record<WhatsAppStatus, string> = {
  pending:   'text-amber-300 bg-amber-500/15 border-amber-500/30',
  active:    'text-emerald-300 bg-emerald-500/15 border-emerald-500/30',
  suspended: 'text-danger bg-danger/15 border-danger/30',
  inactive:  'text-ink-subtle bg-midnight-3/50 border-hairline',
};

export const META_TIER_LABELS: Record<number, string> = {
  1: 'Tier 1 — 1k/dia',
  2: 'Tier 2 — 10k/dia',
  3: 'Tier 3 — 100k/dia',
  4: 'Tier 4 — ilimitado',
};

export interface TenantWhatsApp {
  tenant_id:       string;
  provider:        'ycloud' | 'meta_direct';
  display_phone:   string;
  phone_number_id: string | null;
  waba_id:         string | null;
  status:          WhatsAppStatus;
  tier:            number;
  daily_limit:     number;
  connected_at:    string | null;
  last_health_check: string | null;
  health_status:   string | null;
  created_at:      string;
  updated_at:      string;
}

// ── Queries ───────────────────────────────────────────────────────────────────

export function useTenantWhatsApp(tenantId: string | null) {
  return useQuery<TenantWhatsApp | null>({
    queryKey: ['superadmin', 'tenant-whatsapp', tenantId],
    queryFn: async (): Promise<TenantWhatsApp | null> => {
      if (!tenantId) return null;
      const { data, error } = await db
        .from('tenant_whatsapp')
        .select('*')
        .eq('tenant_id', tenantId)
        .maybeSingle();
      if (error) throw error;
      return (data as TenantWhatsApp | null) ?? null;
    },
    enabled: !!tenantId,
    staleTime: 30_000,
  });
}

// ── Mutações ──────────────────────────────────────────────────────────────────

export interface UpsertWhatsAppInput {
  tenant_id:       string;
  display_phone:   string;
  phone_number_id?: string | null;
  waba_id?:         string | null;
  status?:          WhatsAppStatus;
  tier?:            number;
  daily_limit?:     number;
}

export function useUpsertTenantWhatsApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertWhatsAppInput) => {
      const { error } = await db
        .from('tenant_whatsapp')
        .upsert(
          {
            ...input,
            display_phone:   input.display_phone.trim(),
            phone_number_id: input.phone_number_id?.trim() || null,
            waba_id:         input.waba_id?.trim() || null,
          },
          { onConflict: 'tenant_id' },
        );
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['superadmin', 'tenant-whatsapp', vars.tenant_id] });
      qc.invalidateQueries({ queryKey: ['superadmin', 'all-tenants'] });
    },
  });
}

export function useUpdateWhatsAppStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ tenant_id, status }: { tenant_id: string; status: WhatsAppStatus }) => {
      const { error } = await db
        .from('tenant_whatsapp')
        .update({ status })
        .eq('tenant_id', tenant_id);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['superadmin', 'tenant-whatsapp', vars.tenant_id] });
    },
  });
}

export function useDeleteTenantWhatsApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (tenantId: string) => {
      const { error } = await db
        .from('tenant_whatsapp')
        .delete()
        .eq('tenant_id', tenantId);
      if (error) throw error;
    },
    onSuccess: (_d, tenantId: string) => {
      qc.invalidateQueries({ queryKey: ['superadmin', 'tenant-whatsapp', tenantId] });
    },
  });
}

// ── Plan limits (cooldown) ───────────────────────────────────────────────────

export interface TenantPlanLimits {
  tenant_id:             string;
  min_cooldown_minutes:  number;
  daily_message_limit:   number;
  monthly_message_limit: number;
}

export function useTenantPlanLimits(tenantId: string | null) {
  return useQuery<TenantPlanLimits | null>({
    queryKey: ['superadmin', 'tenant-plan-limits', tenantId],
    queryFn: async () => {
      if (!tenantId) return null;
      const { data, error } = await db
        .from('tenant_plan_limits')
        .select('tenant_id, min_cooldown_minutes, daily_message_limit, monthly_message_limit')
        .eq('tenant_id', tenantId)
        .single();
      if (error) return null;
      return data as TenantPlanLimits;
    },
    enabled: !!tenantId,
  });
}

export function useUpdateTenantPlanLimits() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ tenant_id, ...rest }: Partial<TenantPlanLimits> & { tenant_id: string }) => {
      const { error } = await db
        .from('tenant_plan_limits')
        .upsert({ tenant_id, ...rest }, { onConflict: 'tenant_id' });
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['superadmin', 'tenant-plan-limits', vars.tenant_id] });
    },
  });
}

// ── WhatsApp Templates por tenant ────────────────────────────────────────────

export type HeaderType = 'NONE' | 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT';
export type TemplateStatus = 'pending' | 'approved' | 'rejected' | 'paused';
export type TemplateUseCase =
  | 'checkin' | 'event_confirm' | 'material_received'
  | 'prayer'  | 'leadership'   | 'general';

export interface WhatsAppTemplate {
  id:               string;
  tenant_id:        string;
  name:             string;
  language:         string;
  category:         string;
  body:             string;
  button_labels:    string[];
  status:           TemplateStatus;
  use_case:         TemplateUseCase;
  header_type:      HeaderType;
  header_text:      string | null;
  header_media_url: string | null;
  ycloud_id:        string | null;
  approved_at:      string | null;
  rejection_reason: string | null;
  created_at:       string;
  updated_at:       string;
}

export function useWhatsAppTemplates(tenantId: string | null) {
  return useQuery<WhatsAppTemplate[]>({
    queryKey: ['superadmin', 'wa-templates', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];
      const { data, error } = await db
        .from('whatsapp_templates')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as WhatsAppTemplate[];
    },
    enabled: !!tenantId,
    staleTime: 30_000,
  });
}

export interface UpdateTemplateHeaderInput {
  id:               string;
  tenant_id:        string;
  use_case?:        TemplateUseCase;
  header_type?:     HeaderType;
  header_text?:     string | null;
  header_media_url?: string | null;
}

export function useUpdateWhatsAppTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, tenant_id, ...rest }: UpdateTemplateHeaderInput) => {
      const { error } = await db
        .from('whatsapp_templates')
        .update({ ...rest, updated_at: new Date().toISOString() })
        .eq('id', id)
        .eq('tenant_id', tenant_id);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['superadmin', 'wa-templates', vars.tenant_id] });
    },
  });
}

// ── Messaging settings (janela de envio) ─────────────────────────────────────

export interface TenantMessagingSettings {
  tenant_id:         string;
  send_window_start: string;   // "HH:MM" — TIME stored as string
  send_window_end:   string;   // "HH:MM"
}

export function useTenantMessagingSettings(tenantId: string | null) {
  return useQuery<TenantMessagingSettings | null>({
    queryKey: ['superadmin', 'tenant-messaging-settings', tenantId],
    queryFn: async () => {
      if (!tenantId) return null;
      const { data, error } = await db
        .from('tenant_messaging_settings')
        .select('tenant_id, send_window_start, send_window_end')
        .eq('tenant_id', tenantId)
        .maybeSingle();
      if (error) return null;
      if (!data) return null;
      // Postgres TIME may come as "HH:MM:SS" — truncate to "HH:MM"
      return {
        tenant_id:         data.tenant_id as string,
        send_window_start: (data.send_window_start as string).slice(0, 5),
        send_window_end:   (data.send_window_end   as string).slice(0, 5),
      };
    },
    enabled: !!tenantId,
    staleTime: 30_000,
  });
}

export function useUpsertMessagingSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ tenant_id, send_window_start, send_window_end }: TenantMessagingSettings) => {
      const { error } = await db
        .from('tenant_messaging_settings')
        .upsert({ tenant_id, send_window_start, send_window_end }, { onConflict: 'tenant_id' });
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['superadmin', 'tenant-messaging-settings', vars.tenant_id] });
    },
  });
}
